// @spec TSK-STR-021
// Formatting utilities for dates, numbers, and other values

import i18n, { languageToLocale } from '@/lib/i18n';

/**
 * Get the current locale from i18n for Intl formatting.
 * Falls back to 'en-US' if i18n is not initialized.
 */
function getCurrentLocale(): string {
  return languageToLocale(i18n.language);
}

/**
 * Format a date string for display using the current locale.
 *
 * @param dateStr - ISO date string
 * @returns Formatted date string (e.g., "Jan 21, 2026" for en-US, "21. Jan. 2026" for de-DE)
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(getCurrentLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a datetime string for display using the current locale.
 *
 * @param dateStr - ISO datetime string
 * @returns Formatted datetime string (e.g., "Jan 21, 2026 at 10:30 AM" for en-US)
 */
export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(getCurrentLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Check if a date is overdue (in the past).
 *
 * @param dateStr - ISO date string
 * @returns True if the date is before today
 */
export function isOverdue(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Format a relative time string using Intl.RelativeTimeFormat for locale-aware output.
 *
 * Uses the current i18n locale so output is automatically localized:
 * - en-US: "2 days ago", "in 3 hours"
 * - de-DE: "vor 2 Tagen", "in 3 Stunden"
 *
 * @param dateInput - ISO datetime string or Date object
 * @returns Locale-aware relative time string
 */
export function formatRelativeTime(dateInput: string | Date): string {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const absDiffMs = Math.abs(diffMs);

  const locale = getCurrentLocale();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const absDays = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
  const absHours = Math.floor(absDiffMs / (1000 * 60 * 60));
  const absMinutes = Math.floor(absDiffMs / (1000 * 60));

  if (absDays > 0) {
    return rtf.format(diffMs > 0 ? absDays : -absDays, 'day');
  } else if (absHours > 0) {
    return rtf.format(diffMs > 0 ? absHours : -absHours, 'hour');
  } else if (absMinutes > 0) {
    return rtf.format(diffMs > 0 ? absMinutes : -absMinutes, 'minute');
  }
  return rtf.format(0, 'second');
}

/**
 * Safely coerce a value that may be a Decimal string, number, or null to a
 * JavaScript number. Returns `null` when the value cannot be parsed.
 *
 * Handles the common Pydantic Decimal serialization edge-case where numeric
 * fields arrive as strings (e.g. `"123.45"`) instead of JSON numbers.
 */
export function coerceNumeric(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Format a numeric value for display with locale-aware formatting.
 * Returns a fallback string when the value is null/undefined/NaN.
 */
export function formatNumeric(
  value: string | number | null | undefined,
  decimals = 2,
  fallback = '\u2014'
): string {
  const num = coerceNumeric(value);
  if (num === null) {
    return fallback;
  }
  return num.toLocaleString(getCurrentLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}
